import { useState, useCallback, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationDropdown } from './NotificationDropdown';

interface NotificationBellProps {
  isLearner?: boolean;
}

export function NotificationBell({ isLearner = false }: NotificationBellProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // useNotifications() initializes WebSocket, OneSignal, and fetches initial data
  const { unreadCount } = useNotifications();

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  // Animate bell when new notification arrives
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (unreadCount > 0) {
      setShouldAnimate(true);
      const timeout = setTimeout(() => setShouldAnimate(false), 1000);
      return () => clearTimeout(timeout);
    }
  }, [unreadCount]);

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className={`relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors ${
          isDropdownOpen ? 'bg-gray-100 text-gray-900' : ''
        }`}
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <Bell
          className={`w-5 h-5 ${shouldAnimate ? 'animate-[wiggle_0.5s_ease-in-out]' : ''}`}
        />

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationDropdown
        isOpen={isDropdownOpen}
        onClose={closeDropdown}
        isLearner={isLearner}
      />

      {/* Global animation keyframes */}
      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-15deg); }
          50% { transform: rotate(15deg); }
          75% { transform: rotate(-10deg); }
        }
      `}</style>
    </div>
  );
}

export default NotificationBell;
